import {
  Field, FieldProps, Form, Formik,
} from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import Button from 'creative-components/CustomButtons/Button';
import CustomInput from 'creative-components/CustomInput/CustomInput';
import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import TermsCheckbox from 'components/AgentLanding/TermsCheckbox';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import { trackCustomEvent } from 'components/MetaPixelWrapper';

import { submitAgentLandingContactAgentForm } from 'utils/api';
import { isAgentLandingPageSample, nameRegExp, phoneRegExp } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  submittedMessage: {
    color: theme.palette.white.main,
    textAlign: 'center',
    fontFamily: 'Vidaloka',
    fontSize: '30px',
    lineHeight: '36px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px 0',
  },
  title: {
    color: theme.palette.white.main,
    textAlign: 'center',
    fontFamily: 'Vidaloka',
    fontSize: '36px',
    lineHeight: '42px',
    marginBottom: '20px',
  },
  subtitle: {
    color: theme.palette.white.main,
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: '30px',
  },
  buttonContainer: {
    textAlign: 'center',

    '& > button': {
      margin: '32px 0 0',

      [theme.breakpoints.up('lg')]: {
        minWidth: '400px',
      },
    },
  },
  hidden: {
    visibility: 'hidden',
  },
  input: {
    '& > input': {
    },
  },
  terms: {
    '& > div': {
      color: theme.palette.white.main,

      '& > .Mui-checked': {
        background: `${theme.palette.primary.light} !important`,
      },
    },
  },
}));

interface FormValues {
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
  message: string;
  recaptchaValue: string;
  agreesToTerms: boolean;
}

type Props = {
  agentLandingPageToken: string;
};

const DigitalAdContactForm = ({ agentLandingPageToken }: Props) => {
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;

  const [submitted, setSubmitted] = React.useState(false);

  return (
    <Formik
      initialValues={{
        fullName: '',
        phoneNumber: '',
        email: '',
        address: '',
        message: '',
        recaptchaValue: '',
        agreesToTerms: true,
      } as FormValues}
      onSubmit={async ({
        fullName, phoneNumber, email, address, message, recaptchaValue,
      }) => {
        showLoadingIndicatorModal();

        try {
          // Fire off a custom event for the meta pixel
          trackCustomEvent('ContactFormSubmitted');

          if (!isAgentLandingPageSample(agentLandingPageToken)) {
            await submitAgentLandingContactAgentForm(agentLandingPageToken, fullName, phoneNumber, email, address, message, recaptchaValue);
          }

          setSubmitted(true);
        } catch (error) {
          console.error('error', error);
        }

        hideLoadingIndicatorModal();
      }}
      validationSchema={Yup.object().shape({
        fullName: Yup.string().required().matches(nameRegExp, 'Name is not valid'),
        phoneNumber: Yup.string().required().matches(phoneRegExp, 'Phone number is not valid'),
        email: Yup.string().email().required(),
        address: Yup.string().max(100),
        message: Yup.string().max(500),
        recaptchaValue: Yup.string(), // .required(),
        agreesToTerms: Yup.boolean().isTrue().required(),
      })}
    >
      {(props) => {
        const { handleSubmit, isSubmitting, values } = props;

        return (
          <Form>
            {!submitted ? (
              <GridContainer>
                <GridItem xs={12}>
                  <h3 className={classes.title}>Want Your Real-Time Home Valuation?</h3>
                  <h3 className={classes.subtitle}>I'll send you a custom report with pricing insights based on your exact address</h3>
                </GridItem>
                <GridItem xs={12} lg={6}>
                  <Field name="fullName">
                    {({ field, meta }: FieldProps) => (
                      <CustomInput
                        labelBold
                        labelRequired
                        inputProps={{
                          ...field,
                          placeholder: 'Full Name',
                          autoComplete: 'name',
                          maxLength: 100,
                        }}
                        error={!!meta.touched && meta.error !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12} lg={6}>
                  <Field name="phoneNumber">
                    {({ field, meta }: FieldProps) => (
                      <CustomInput
                        labelBold
                        labelRequired
                        inputProps={{
                          ...field,
                          placeholder: 'Phone Number',
                          type: 'tel',
                          autoComplete: 'tel',
                        }}
                        mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        error={!!meta.touched && meta.error !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12}>
                  <Field name="email">
                    {({ field, meta }: FieldProps) => (
                      <CustomInput
                        labelBold
                        labelRequired
                        inputProps={{
                          ...field,
                          placeholder: 'Email Address',
                          autoComplete: 'email',
                        }}
                        error={!!meta.touched && meta.error !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12}>
                  <Field name="address">
                    {({ field, meta }: FieldProps) => (
                      <CustomInput
                        labelBold
                        labelRequired
                        inputProps={{
                          ...field,
                          placeholder: 'Address',
                          autoComplete: 'street-address',
                        }}
                        error={!!meta.touched && meta.error !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12}>
                  <Field name="message">
                    {({ field, meta }: FieldProps) => (
                      <CustomInput
                        labelBold
                        inputProps={{
                          ...field,
                          placeholder: 'Message',
                          multiline: true,
                          rows: 6,
                          className: classes.input,
                          maxLength: 500,
                        }}
                        error={!!meta.touched && meta.error !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                {/* <GridItem xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                  <Field name="recaptchaValue">
                    {({ field, meta }: FieldProps) => (
                      <ReCAPTCHAInput
                        fieldName={field.name}
                      />
                    )}
                  </Field>
                </GridItem> */}
                <GridItem xs={12} style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }} className={classes.terms}>
                  <Field name="agreesToTerms">
                    {({ field, meta }: FieldProps) => (
                      <TermsCheckbox fieldName={field.name} />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12} className={classes.buttonContainer}>
                  <Button
                    color="dark"
                    round
                    size="large"
                    disabled={isSubmitting || !values.agreesToTerms}
                    onClick={() => handleSubmit()}
                  >
                    Submit
                  </Button>
                </GridItem>
              </GridContainer>
            ) : (
              <Box className={classes.submittedMessage}>
                Thank you for your message, I will be in touch!
              </Box>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default DigitalAdContactForm;
